/**
 * Pagina de estado de sincronizacion (Sprint 4 G).
 *
 * Muestra:
 *   - Estado actual (online/offline, ultima sync, error)
 *   - Contadores de cambios pendientes y fotos pendientes
 *   - Listado detallado de cambios pendientes con su tipo, antiguedad
 *     e intentos fallidos para diagnostico
 *   - Boton "Sincronizar ahora" manual
 *
 * No se accede por nav principal todavia; se enlaza desde el banner.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import type { LocalPendingChange, LocalPhotoQueueItem } from '../offline/db.js';
import { listPending, listPendingPhotos } from '../offline/repository.js';
import { type SyncState, subscribeSyncState, syncNow } from '../offline/sync.js';

export function SyncStatusPage(): JSX.Element {
  const navigate = useNavigate();
  const { setBackTo } = useAppShell();
  const [state, setState] = useState<SyncState | null>(null);

  // Back button (BB-UX-01)
  useEffect(() => {
    setBackTo({ href: '/clients', label: 'Clientes' });
    return () => setBackTo(null);
  }, [setBackTo]);
  const [changes, setChanges] = useState<LocalPendingChange[]>([]);
  const [photos, setPhotos] = useState<LocalPhotoQueueItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => subscribeSyncState(setState), []);

  async function refresh(): Promise<void> {
    const [c, p] = await Promise.all([
      listPending().catch(() => []),
      listPendingPhotos().catch(() => []),
    ]);
    setChanges(c);
    setPhotos(p);
  }

  useEffect(() => {
    void refresh();
  }, [state?.pendingChanges, state?.pendingPhotos, state?.syncing]);

  async function handleSync(): Promise<void> {
    setLoading(true);
    await syncNow();
    setLoading(false);
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 space-y-5">
      <header>
        <p className="text-xs uppercase tracking-widest text-slate-400">Sincronizacion</p>
        <h1 className="text-xl font-semibold text-brand-900">Estado offline</h1>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-2">
        <Stat label="Conectividad" value={state?.online ? 'Online' : 'Offline'} />
        <Stat
          label="Ultima sync"
          value={
            state?.lastSyncedAt
              ? new Date(state.lastSyncedAt).toLocaleString('es-DO')
              : 'Nunca en esta sesion'
          }
        />
        <Stat label="Cambios pendientes" value={String(state?.pendingChanges ?? 0)} />
        <Stat label="Fotos pendientes" value={String(state?.pendingPhotos ?? 0)} />
        {state?.lastError && (
          <div className="sm:col-span-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {state.lastError}
          </div>
        )}
      </section>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={loading || !state?.online}
          onClick={() => {
            void handleSync();
          }}
          className="flex-1 rounded-lg bg-brand-700 px-4 py-3 text-sm font-medium text-white disabled:bg-slate-300"
        >
          {loading ? 'Sincronizando...' : 'Sincronizar ahora'}
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Volver
        </button>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-700">
          Cambios encolados ({changes.length})
        </h2>
        {changes.length === 0 ? (
          <p className="text-xs text-slate-400">Sin cambios pendientes.</p>
        ) : (
          <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
            {changes.map((c) => (
              <li key={c.clientChangeId} className="px-3 py-2 text-xs">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-800">{c.kind}</span>
                  <span className="text-slate-500">
                    {new Date(c.ts).toLocaleTimeString('es-DO')}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-slate-400">
                  {c.clientChangeId.slice(0, 8)} · assessment {c.assessmentId.slice(0, 8)} ·{' '}
                  intentos {c.attempts}
                </div>
                {c.lastError && <p className="text-red-600">{c.lastError}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-700">Fotos en cola ({photos.length})</h2>
        {photos.length === 0 ? (
          <p className="text-xs text-slate-400">Sin fotos pendientes.</p>
        ) : (
          <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
            {photos.map((p) => (
              <li key={p.id} className="px-3 py-2 text-xs">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-800">{p.questionRef}</span>
                  <span className="text-slate-500">{p.status}</span>
                </div>
                <div className="font-mono text-[10px] text-slate-400">
                  {p.id.slice(0, 8)} · {p.contentType} ·{' '}
                  {new Date(p.capturedAt).toLocaleTimeString('es-DO')}
                </div>
                {p.lastError && <p className="text-red-600">{p.lastError}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
}
